/**
 * The THREE.Skeleton a scene Skeleton3D's skin gives a mesh. A skinned mesh draws detached from its
 * skeleton, so each bone carries the skin matrix of its bind in its world matrix, in mesh space,
 * and an identity inverse: three then skins a vertex by the weighted sum of those matrices, as
 * `skeleton.glsl:248-291` does. An AnimationPlayer that writes bone poses to the Skeleton3D moves
 * the skin in place, through the matrices of the bones, and builds nothing again.
 */
import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useAnimatedBonePoses } from '../contexts/AnimatedBonePosesContext.js';
import { setMatrixFromTransform3D } from '../nodeTreeTransforms.js';
import { sameData } from '../../utils/sameData.js';
import { useByValue } from '../hooks/useByValue.js';
import { surfaceBoneCounts } from '../../resources/meshes/godotSurface.js';
import { useDisposeOnRelease } from '../useDisposeOnRelease.js';
/**
 * A bone of a skin with no bind at all. The bone buffer Godot allocates for it stays zero
 * (`mesh_storage.cpp:2358`), and a zero matrix collapses the vertex.
 */
const MISSING_BONE_MATRIX = new THREE.Matrix4().set(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
/** How many bones the skin indices of the geometry's surfaces address: the most any one does. */
function boneCountOf(geometry) {
    return Math.max(0, ...(surfaceBoneCounts(geometry) ?? []));
}
/**
 * Whether Godot draws nothing of the mesh: every surface addresses more bones than the skin binds.
 * `mesh_get_aabb` skips such a surface (`mesh_storage.cpp:729-730`), so an instance whose surfaces
 * are all skipped has no bounds, and `_update_instance` never places it (`renderer_scene_cull.cpp:1679`).
 * A skin with no bind keeps the mesh's own bounds (`mesh_storage.cpp:716`), and its vertices collapse.
 */
export function isOutgrownBySkin(geometry, bindCount) {
    const boneCounts = geometry && surfaceBoneCounts(geometry);
    if (!boneCounts || bindCount === 0)
        return false;
    return boneCounts.every((boneCount) => boneCount > bindCount);
}
function writeBoneMatrix(target, skinMatrix) {
    if (skinMatrix)
        setMatrixFromTransform3D(target, skinMatrix);
    else
        target.copy(MISSING_BONE_MATRIX);
}
function boneOf(skinMatrix) {
    const bone = new THREE.Bone();
    // Never in a scene graph, so nothing recomputes the world matrix written here.
    bone.matrixAutoUpdate = false;
    bone.matrixWorldAutoUpdate = false;
    writeBoneMatrix(bone.matrixWorld, skinMatrix);
    return bone;
}
/**
 * A skeleton whose bones move only when `writeSkin` says so: `update` leaves them, so the renderer
 * neither multiplies every bone nor uploads the bone texture again each frame.
 */
class SkinSkeleton extends THREE.Skeleton {
    constructor(bones) {
        super(bones, bones.map(() => new THREE.Matrix4()));
        super.update();
    }
    update() { }
    /** Moves each bone to its matrix of `skinMatrices` and fills the bone matrices from them, in place. */
    writeSkin(skinMatrices) {
        this.bones.forEach((bone, i) => writeBoneMatrix(bone.matrixWorld, skinMatrices[i]));
        super.update();
    }
}
function sameMatrices(a, b) {
    return sameData(a, b);
}
/**
 * The skeleton of the `skin` of a Skeleton3D for the skinned `geometry`, or null for a geometry with
 * no bone arrays or a node that binds no skeleton. It takes the bone poses that an AnimationPlayer
 * writes to the Skeleton3D, and the file pose again once they are released. Disposed once nothing
 * draws it.
 *
 * @canonical The THREE.Skeleton a scene Skeleton3D gives a mesh
 */
export function useSkinSkeleton(geometry, skin) {
    // By value, so a re-parse that builds the same skin again does not rebuild the skeleton.
    const stableMatrices = useByValue(skin?.matrices, sameMatrices);
    const skeleton = useMemo(() => {
        if (!geometry?.getAttribute('skinIndex') || !stableMatrices)
            return null;
        const bones = Array.from({ length: boneCountOf(geometry) }, (_unused, i) => boneOf(stableMatrices[i]));
        return new SkinSkeleton(bones);
    }, [geometry, stableMatrices]);
    useDisposeOnRelease(skeleton);
    useAnimatedPose(skeleton, skin);
    return skeleton;
}
/**
 * Follows the poses written to the Skeleton3D of `skin`, starting on the ones already written, and
 * writes the file pose back where they are released.
 */
function useAnimatedPose(skeleton, skin) {
    const poses = useAnimatedBonePoses();
    useEffect(() => {
        if (!skeleton || !skin)
            return;
        const follow = (pose) => skeleton.writeSkin(pose ? skin.matricesUnder(pose) : skin.matrices);
        return poses.subscribe(skin.skeletonPath, follow);
    }, [skeleton, skin, poses]);
}
//# sourceMappingURL=skinSkeleton.js.map